import React from 'react';
import ReactDOM from 'react-dom/client';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import { MsalProvider } from '@azure/msal-react';
import { PublicClientApplication } from '@azure/msal-browser';
import App from './App';
import { store } from './store';
import { initializeMsal, validateEntraConfig } from './config/authConfig';
import { AnalyticsProvider } from './contexts/AnalyticsContext';
import { CookieConsentBanner } from './components/common/CookieConsentBanner/CookieConsentBanner';
import { ErrorBoundary } from './components/common/ErrorBoundary';
import { startVersionCheck } from './utils/versionCheck';
import { logger } from './utils/logger';
import './index.css';

// Catch unhandled promise rejections and route them to the logger
window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
  const reason = event.reason instanceof Error ? event.reason : new Error(String(event.reason));
  logger.error('Unhandled promise rejection', reason);
  event.preventDefault();
});

// Validate Entra External ID configuration
try {
  validateEntraConfig();
} catch (error) {
  logger.fatal('Failed to validate Entra configuration', error, {
    event: 'frontend.bootstrap.config_failed',
  });
}

// MSAL instance - will be initialized before rendering
let msalInstance: PublicClientApplication;

// Get root element
const rootElement = document.getElementById('root');

if (!rootElement) {
  throw new Error('Failed to find the root element');
}

// Create root
const root = ReactDOM.createRoot(rootElement);

/**
 * Initialize MSAL and render the app
 *
 * MSAL v4.x requires calling initialize() before any other MSAL API calls.
 * This must happen before MsalProvider is rendered.
 */
const initializeAndRender = async () => {
  try {
    // Initialize MSAL (required in v4.x - must be called before any other MSAL API)
    msalInstance = await initializeMsal();

    // Render the app with initialized MSAL instance
    root.render(
      <React.StrictMode>
        <ErrorBoundary>
          <MsalProvider instance={msalInstance}>
            <Provider store={store}>
              <BrowserRouter>
                <AnalyticsProvider>
                  <App />
                  <CookieConsentBanner />
                </AnalyticsProvider>
              </BrowserRouter>
            </Provider>
          </MsalProvider>
        </ErrorBoundary>
      </React.StrictMode>
    );
  } catch (error) {
    logger.fatal('Failed to initialize MSAL', error, {
      event: 'frontend.bootstrap.msal_failed',
    });
    // Render error state
    root.render(
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center px-4">
          <h1 className="text-2xl font-bold text-red-600 mb-4">
            Authentication Configuration Error
          </h1>
          <p className="text-gray-600 mb-4">
            Failed to initialize authentication. Please check the console for details.
          </p>
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }
};

// Start initialization
startVersionCheck();
initializeAndRender();
